<template>
  <div
    ref="viewport"
    class="dotted-world-map"
    :class="{ 'dotted-world-map--pannable': canPan, 'dotted-world-map--panning': panning }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
  >
    <!--
      Land, rasterised from the vector outlines at the current size and zoom.

      On a canvas rather than as SVG circles: the lattice is anchored to the viewport, so panning
      changes which cells are lit instead of moving existing dots, and up to ~12k <circle> elements
      would each take a real attribute write on every frame. One bitmap is one draw call, and the
      element's own `color` / `background-color` carry the two colours so a CSS variable still resolves
      through the cascade as it did on the SVG attributes.
    -->
    <canvas
      v-if="size.width > 0 && size.height > 0"
      ref="landCanvas"
      class="dotted-world-map__land"
      :style="{ color: dotColor, backgroundColor: waterColor }"
    ></canvas>

    <svg
      v-if="size.width > 0 && size.height > 0"
      class="dotted-world-map__canvas"
      :viewBox="`0 0 ${size.width} ${size.height}`"
      role="img"
      :aria-label="ariaLabel"
    >
      <path
        v-for="(arc, index) in arcPaths"
        :key="`a${index}`"
        :d="arc.d"
        :stroke="arc.color"
        :stroke-width="arc.width"
        :opacity="arc.opacity"
        fill="none"
      />

      <!-- Halo, then ring, then the dot itself: outermost layer first so each one reads as a
           backdrop for the next. -->
      <g
        v-for="node in placedNodes"
        :key="node.id"
        class="dotted-world-map__node"
        @mouseenter="hoveredId = node.id"
        @mouseleave="hoveredId = null"
      >
        <!-- Bottom of the stack and transparent: a dependable pointer target for a marker only a few
             pixels wide. Painted first so the visible layers still read outermost-first. -->
        <circle :cx="node.x" :cy="node.y" :r="node.hitDiameter / 2" fill="transparent" />
        <circle
          v-if="node.haloDiameter"
          :cx="node.x"
          :cy="node.y"
          :r="node.haloDiameter / 2"
          :fill="node.haloColor"
          :opacity="node.haloOpacity"
        />
        <circle
          v-if="node.ringDiameter"
          :cx="node.x"
          :cy="node.y"
          :r="node.ringDiameter / 2"
          :fill="node.ringColor"
        />
        <!-- Marks a marker standing for several places, so a merge is visible on the map itself and
             not only once the pointer has found it. Under the dot, so the dot stays the top layer. -->
        <circle
          v-if="isCluster(node)"
          :cx="node.x"
          :cy="node.y"
          :r="node.diameter / 2 + 3"
          fill="none"
          :stroke="node.color"
          stroke-width="1"
          opacity="0.55"
        />
        <circle :cx="node.x" :cy="node.y" :r="node.diameter / 2" :fill="node.color" />
      </g>
    </svg>

    <div
      v-if="hovered"
      class="dotted-world-map__tooltip"
      :style="{ left: `${hovered.x}px`, top: `${hovered.y - tooltipOffset}px` }"
    >
      <slot name="tooltip" :node="hovered.source" :nodes="hovered.sources" />
    </div>

    <div
      v-if="zoomControls && maxZoomSteps > 0"
      class="dotted-world-map__zoom"
      :class="`dotted-world-map__zoom--${zoomControlsPosition}`"
    >
      <button
        type="button"
        :aria-label="zoomInLabel"
        :title="zoomInLabel"
        :disabled="!canZoomIn"
        @pointerdown.stop
        @click="zoomIn"
      >
        +
      </button>
      <button
        type="button"
        :aria-label="zoomOutLabel"
        :title="zoomOutLabel"
        :disabled="!canZoomOut"
        @pointerdown.stop
        @click="zoomOut"
      >
        &minus;
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref, watch, type PropType } from "vue";
import { INLAND_WATER, LAT_NORTH, LAT_SOUTH, WORLD_LAND } from "./world-land";
import type { DottedMapArc, DottedMapNode, LandRing, PlacedNode } from "./types";

/** Longitude always spans the whole globe; only latitude is ever cropped. */
const LON_WEST = -180;
const LON_EAST = 180;
const LON_SPAN = LON_EAST - LON_WEST;

/**
 * Latitude buckets used to index polygon edges. Enough that a bucket is a fraction of a degree, so
 * a scanline reads only the edges near it, while the index itself stays small.
 */
const LAT_BUCKETS = 256;

/** Clear space between a marker's edge and its tooltip, in pixels. */
const TOOLTIP_GAP = 4;

/**
 * How far an axis must overflow before the map calls itself pannable.
 *
 * The box is measured with `Math.floor`, so under a cover fit the un-fitted axis almost always
 * overflows by a fraction of a pixel — enough for a strict comparison to advertise a grab cursor, take
 * the pointer and, with `touch-action: none`, swallow a page scroll, all for a pan that moves nothing.
 * A whole pixel, because a pan range narrower than one is not a pan at any zoom level.
 */
const PAN_EPSILON = 1;

/**
 * Narrowest scanline span that still earns a dot when it falls between two lattice columns.
 *
 * Without a floor, a scanline touching a ring's local minimum produces two identical crossings and a
 * zero-width span, which would then be drawn at the same weight as a continent.
 */
const MIN_SPAN_FRACTION = 8;

interface Dot {
  x: number;
  y: number;
}

interface EdgeIndex {
  lonA: number[];
  latA: number[];
  lonB: number[];
  latB: number[];
  buckets: number[][];
  south: number;
  span: number;
}

interface RingMetrics {
  latMin: number;
  latMax: number;
  lon: number;
  lat: number;
  area: number;
}

/**
 * Derived geometry, cached against the identity of the outline arrays that produced it.
 *
 * Both are pure functions of module-level constants in practice, but they were per-instance computeds:
 * building the edge index walks ~16k vertices and retains ~1 MB, and the host keeps the map behind a
 * `v-if` that flips on every refresh — so every period change used to throw the index away and derive
 * it again for data that had not changed.
 */
const edgeIndexCache = new WeakMap<LandRing[], Map<string, EdgeIndex>>();
const ringMetricsCache = new WeakMap<LandRing[], RingMetrics[]>();
const ringsIds = new WeakMap<LandRing[], number>();
let nextRingsId = 0;

/** Stable identity for a ring array, so it can take part in a string cache key. */
const ringsId = (rings: LandRing[]) => {
  const existing = ringsIds.get(rings);
  if (existing !== undefined) return existing;
  nextRingsId += 1;
  ringsIds.set(rings, nextRingsId);

  return nextRingsId;
};

const buildEdgeIndex = (
  land: LandRing[],
  water: LandRing[],
  latSouth: number,
  latNorth: number
): EdgeIndex => {
  const lonA: number[] = [];
  const latA: number[] = [];
  const lonB: number[] = [];
  const latB: number[] = [];
  const buckets: number[][] = Array.from({ length: LAT_BUCKETS }, () => []);
  const span = Math.max(1e-9, latNorth - latSouth);

  for (const ring of [...land, ...water]) {
    for (let i = 0; i < ring.length; i++) {
      const [lon1, lat1] = ring[i];
      const [lon2, lat2] = ring[(i + 1) % ring.length];
      // A horizontal edge can never satisfy the half-open crossing test, and would divide by
      // zero if it somehow did.
      if (lat1 === lat2) continue;

      const low = lat1 < lat2 ? lat1 : lat2;
      const high = lat1 < lat2 ? lat2 : lat1;
      if (high < latSouth || low > latNorth) continue;

      const edge = lonA.length;
      lonA.push(lon1);
      latA.push(lat1);
      lonB.push(lon2);
      latB.push(lat2);

      const bucketOf = (latitude: number) =>
        Math.min(
          LAT_BUCKETS - 1,
          Math.max(0, Math.floor(((latitude - latSouth) / span) * LAT_BUCKETS))
        );
      // Both ends clamped the same way: a vertex sitting exactly on latNorth divides out to 1, and an
      // unclamped floor then puts the edge in bucket 256 — past the last one, so no scanline can ever
      // reach it and its ring loses a crossing.
      const from = bucketOf(low);
      const to = bucketOf(high);
      for (let bucket = from; bucket <= to; bucket++) buckets[bucket].push(edge);
    }
  }

  return { lonA, latA, lonB, latB, buckets, south: latSouth, span };
};

const cachedEdgeIndex = (
  land: LandRing[],
  water: LandRing[],
  latSouth: number,
  latNorth: number
): EdgeIndex => {
  let byBand = edgeIndexCache.get(land);
  if (!byBand) {
    byBand = new Map();
    edgeIndexCache.set(land, byBand);
  }
  // The band is part of the key because it decides which edges are kept and how they are bucketed.
  const key = `${ringsId(water)}:${latSouth}:${latNorth}`;
  const existing = byBand.get(key);
  if (existing) return existing;

  const built = buildEdgeIndex(land, water, latSouth, latNorth);
  byBand.set(key, built);

  return built;
};

const buildRingMetrics = (land: LandRing[]): RingMetrics[] =>
  land.map((ring) => {
    let latMin = Infinity;
    let latMax = -Infinity;
    let lonSum = 0;
    let latSum = 0;
    let twiceArea = 0;
    let centroidLon = 0;
    let centroidLat = 0;
    for (let i = 0; i < ring.length; i++) {
      const [lon, lat] = ring[i];
      const [nextLon, nextLat] = ring[(i + 1) % ring.length];
      if (lat < latMin) latMin = lat;
      if (lat > latMax) latMax = lat;
      lonSum += lon;
      latSum += lat;
      const cross = lon * nextLat - nextLon * lat;
      twiceArea += cross;
      centroidLon += (lon + nextLon) * cross;
      centroidLat += (lat + nextLat) * cross;
    }

    // The true polygon centroid, not the vertex mean: the mean is pulled towards wherever vertices are
    // dense, which for a concave outline - Iceland, Flores, New Britain - puts the island's only dot a
    // lattice cell out into open water. It costs nothing here, since the signed area is already summed.
    const degenerate = twiceArea === 0;

    return {
      latMin,
      latMax,
      lon: degenerate ? lonSum / ring.length : centroidLon / (3 * twiceArea),
      lat: degenerate ? latSum / ring.length : centroidLat / (3 * twiceArea),
      area: Math.abs(twiceArea) / 2,
    };
  });

const cachedRingMetrics = (land: LandRing[]): RingMetrics[] => {
  const existing = ringMetricsCache.get(land);
  if (existing) return existing;

  const built = buildRingMetrics(land);
  ringMetricsCache.set(land, built);

  return built;
};

interface RenderedNode extends PlacedNode<DottedMapNode> {
  color: string;
  diameter: number;
  /** Pointer target, never smaller than `nodeHitDiameter`. */
  hitDiameter: number;
  ringDiameter: number;
  ringColor: string;
  haloDiameter: number;
  haloColor: string;
  haloOpacity: number;
}

/**
 * A dotted world map rendered from vector outlines.
 *
 * The land is stored as polygons and rasterised into a dot lattice at draw time, rather than being
 * a pre-baked image or point set. That is what makes zoom honest: the lattice is built in screen
 * space, so dots keep exactly the same pixel spacing and size at every zoom level and only the
 * projection changes — zooming in reveals more coastline detail instead of magnifying dots.
 */
export default defineComponent({
  props: {
    /** Land outlines. Rings enclosed by other rings become holes (even-odd fill). */
    land: {
      type: Array as PropType<LandRing[]>,
      default: () => WORLD_LAND,
    },
    /** Inland water, drawn as holes punched through the land. */
    water: {
      type: Array as PropType<LandRing[]>,
      default: () => INLAND_WATER,
    },
    latNorth: {
      type: Number,
      default: LAT_NORTH,
    },
    latSouth: {
      type: Number,
      default: LAT_SOUTH,
    },
    /**
     * How the world is fitted into the box at the outermost zoom level.
     *
     * `contain` keeps all of it on screen and shows the surplus as sea. `cover` fills the box and lets
     * the wider axis run off the edges, where panning reaches it — the right choice when the box is a
     * different shape from the world and empty bands would otherwise dominate it.
     */
    fit: {
      type: String as PropType<"contain" | "cover">,
      default: "contain",
    },

    /** Distance between neighbouring dots, in screen pixels. Constant across zoom levels. */
    dotSpacing: {
      type: Number,
      default: 6,
    },
    /** Dot size in screen pixels. Derived from the spacing when left null. */
    dotDiameter: {
      type: Number as PropType<number | null>,
      default: null,
    },
    dotColor: {
      type: String,
      default: "rgb(var(--v-theme-indigo-light))",
    },
    /** Colour of the sea, i.e. the map's background. Transparent shows whatever is behind it. */
    waterColor: {
      type: String,
      default: "transparent",
    },

    nodes: {
      type: Array as PropType<DottedMapNode[]>,
      default: () => [],
    },
    nodeColor: {
      type: String,
      default: "rgb(var(--v-theme-primary))",
    },
    nodeDiameter: {
      type: Number,
      default: 10,
    },
    /** Default colour of the ring around a node. Defaults to white so the node reads as cut out. */
    nodeRingColor: {
      type: String,
      default: "rgb(var(--v-theme-white))",
    },
    /**
     * Default halo colour. Null rather than an empty string on purpose: the fallback chain is written
     * with `??`, which only steps past null and undefined, so an empty default would be a valid answer
     * and would end the chain before it reached the node's own colour — leaving `fill=""`, which SVG
     * ignores in favour of its initial value, black.
     */
    nodeHaloColor: {
      type: String as PropType<string | null>,
      default: null,
    },
    nodeHaloOpacity: {
      type: Number,
      default: 0.18,
    },

    /**
     * Places markers on the same lattice the land dots use, instead of at their exact projected
     * position. Off by at most half a cell, and it stops a marker reading as floating between dots.
     */
    snapNodes: {
      type: Boolean,
      default: false,
    },
    /**
     * Draws markers that would overlap as a single one, with every node behind it handed to the
     * tooltip. Without this, locations close together at low zoom pile up into something the pointer
     * cannot separate: whichever marker happens to be on top answers every hover.
     */
    clusterNodes: {
      type: Boolean,
      default: false,
    },
    /** Smallest pointer target for a marker, in pixels. Markers smaller than this stay easy to hit. */
    nodeHitDiameter: {
      type: Number,
      default: 20,
    },

    arcs: {
      type: Array as PropType<DottedMapArc[]>,
      default: () => [],
    },
    arcColor: {
      type: String,
      default: "rgb(var(--v-theme-indigo-medium))",
    },
    arcWidth: {
      type: Number,
      default: 1,
    },
    arcOpacity: {
      type: Number,
      default: 0.6,
    },
    /** How far an arc bows away from the straight chord, as a fraction of the chord's length. */
    arcBow: {
      type: Number,
      default: 0.22,
    },

    /** Multiplier applied per zoom step. */
    zoomStep: {
      type: Number,
      default: 2,
    },
    /** How many times the user may zoom in. Zero hides the controls entirely. */
    maxZoomSteps: {
      type: Number,
      default: 3,
    },
    zoomControls: {
      type: Boolean,
      default: true,
    },
    zoomControlsPosition: {
      type: String as PropType<"bottom-left" | "bottom-right" | "top-left" | "top-right">,
      default: "bottom-left",
    },
    /**
     * The three accessible labels, required rather than defaulted.
     *
     * This component carries no i18n of its own by design, so a default here could only be an English
     * string shipped to every locale. Requiring them puts the wording where the translations are.
     *
     * `ariaLabel` has to be bound camelCased by the consumer: hyphenated, it is read as the native
     * `aria-label` attribute rather than as this prop.
     */
    zoomInLabel: {
      type: String,
      required: true as const,
    },
    zoomOutLabel: {
      type: String,
      required: true as const,
    },
    ariaLabel: {
      type: String,
      required: true as const,
    },
  },
  setup(props) {
    const viewport = ref<HTMLElement | null>(null);
    const landCanvas = ref<HTMLCanvasElement | null>(null);
    const size = ref({ width: 0, height: 0 });
    /**
     * Held as an id, not as the object.
     *
     * `placedNodes` allocates fresh objects whenever the scale, the centre or the data change, so a
     * captured snapshot would keep the position and payload it had on `mouseenter` — the tooltip would
     * stay behind while its marker moved, and outlive a marker dropped by a refresh.
     */
    const hoveredId = ref<string | null>(null);

    /* ------------------------------------------------------------------ measurement */

    let observer: ResizeObserver | null = null;

    const measure = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      size.value = { width: Math.floor(rect.width), height: Math.floor(rect.height) };
    };

    /**
     * Bound through a watcher rather than `onMounted` because the host commonly keeps this component
     * behind a `v-if` while it loads. In that case the element does not exist at mount, and binding
     * there would leave the map unmeasured — and therefore blank — for good.
     */
    watch(
      viewport,
      (element) => {
        observer?.disconnect();
        observer = null;
        if (!element) return;
        // Measured up front so the first paint already has a size, rather than one blank frame.
        measure(element);
        if (typeof ResizeObserver === "undefined") return;
        observer = new ResizeObserver((entries) => {
          const box = entries[0]?.contentRect;
          if (box) {
            size.value = { width: Math.floor(box.width), height: Math.floor(box.height) };
          }
        });
        observer.observe(element);
      },
      { immediate: true, flush: "post" }
    );

    onBeforeUnmount(() => {
      observer?.disconnect();
      observer = null;
    });

    /* ------------------------------------------------------------------ projection */

    const latSpan = computed(() => Math.max(1, props.latNorth - props.latSouth));
    const homeCenter = computed(() => ({
      lon: (LON_WEST + LON_EAST) / 2,
      lat: (props.latNorth + props.latSouth) / 2,
    }));

    const level = ref(0);
    const zoom = computed(() => Math.pow(props.zoomStep, level.value));

    /**
     * One scale for both axes, so the map keeps atlas proportions instead of being stretched to the
     * container. What differs between the two fits is which axis gets to decide it.
     *
     * `contain` shows the whole world at once and leaves the surplus as sea — correct, but a box that
     * is taller than the world's own 360:134 shape then carries deep empty bands above and below, and
     * no latitude range can close that gap (filling a 16:9 box across a full 360 degrees would need
     * 200 degrees of latitude). `cover` gives that space to the map instead: the shorter axis is
     * filled exactly and the longer one overflows, to be reached by panning.
     */
    const fitScale = computed(() => {
      const byWidth = size.value.width / LON_SPAN;
      const byHeight = size.value.height / latSpan.value;
      return props.fit === "cover" ? Math.max(byWidth, byHeight) : Math.min(byWidth, byHeight);
    });

    const scale = computed(() => fitScale.value * zoom.value);

    const requestedCenter = ref({ ...homeCenter.value });

    /**
     * Keeps the view inside the world. An axis whose full extent already fits is pinned to the
     * middle, so a zoomed-out map cannot be dragged off into empty space; an axis that overflows is
     * clamped to its edges, so panning stops at the coastline rather than at nothing.
     */
    const center = computed(() => {
      const s = scale.value;
      const requested = requestedCenter.value;
      if (!(s > 0)) return homeCenter.value;

      const halfLon = size.value.width / 2 / s;
      const halfLat = size.value.height / 2 / s;

      const lon =
        LON_SPAN * s <= size.value.width
          ? homeCenter.value.lon
          : Math.min(LON_EAST - halfLon, Math.max(LON_WEST + halfLon, requested.lon));
      const lat =
        latSpan.value * s <= size.value.height
          ? homeCenter.value.lat
          : Math.min(props.latNorth - halfLat, Math.max(props.latSouth + halfLat, requested.lat));

      return { lon, lat };
    });

    const projectX = (lon: number) => size.value.width / 2 + (lon - center.value.lon) * scale.value;
    const projectY = (lat: number) =>
      size.value.height / 2 - (lat - center.value.lat) * scale.value;
    /** Inverse of {@link projectY}, needed to walk the lattice row by row. */
    const latFromY = (y: number) => center.value.lat - (y - size.value.height / 2) / scale.value;

    /* ------------------------------------------------------------------ zoom and pan */

    const canZoomIn = computed(() => level.value < props.maxZoomSteps);
    const canZoomOut = computed(() => level.value > 0);

    const zoomIn = () => {
      if (canZoomIn.value) level.value += 1;
    };

    const zoomOut = () => {
      if (!canZoomOut.value) return;
      level.value -= 1;
      // The pan is deliberately kept: under a cover fit an axis can still overflow at level 0, so
      // there is a real position to return to. Where an axis does fit, the clamp already pins it to
      // the middle and the remembered offset has no effect until it is zoomed back in.
    };

    const canPan = computed(
      () =>
        scale.value > 0 &&
        (LON_SPAN * scale.value > size.value.width + PAN_EPSILON ||
          latSpan.value * scale.value > size.value.height + PAN_EPSILON)
    );

    const panning = ref(false);
    let lastPointer = { x: 0, y: 0 };
    let pendingPointer: { x: number; y: number } | null = null;
    let panFrame = 0;

    /**
     * Pointer capture keeps a drag alive when the cursor leaves the map, but it throws on a pointer
     * id the platform does not recognise. It is an enhancement, never a precondition for panning.
     */
    const withPointerCapture = (event: PointerEvent, take: boolean) => {
      const target = event.currentTarget as Element | null;
      try {
        if (take) target?.setPointerCapture?.(event.pointerId);
        else target?.releasePointerCapture?.(event.pointerId);
      } catch {
        // Ignored on purpose: panning works without capture.
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!canPan.value) return;
      panning.value = true;
      lastPointer = { x: event.clientX, y: event.clientY };
      withPointerCapture(event, true);
    };

    /**
     * Applies whatever the pointer last reported, once per displayed frame.
     *
     * Trackpads sample at 120–240 Hz and each raw event invalidates the projection, which re-rasterises
     * the whole lattice — so writing the centre per event buys two to four full rebuilds for every frame
     * that is actually painted. The delta is measured against the pointer position, not accumulated, so
     * coalescing loses no movement.
     */
    const commitPan = () => {
      panFrame = 0;
      const pointer = pendingPointer;
      pendingPointer = null;
      if (!pointer || !panning.value) return;
      const dx = pointer.x - lastPointer.x;
      const dy = pointer.y - lastPointer.y;
      lastPointer = pointer;
      // Started from the clamped centre, so a drag that runs into an edge does not build up an
      // invisible offset that has to be dragged back out.
      requestedCenter.value = {
        lon: center.value.lon - dx / scale.value,
        lat: center.value.lat + dy / scale.value,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!panning.value) return;
      pendingPointer = { x: event.clientX, y: event.clientY };
      if (!panFrame) panFrame = requestAnimationFrame(commitPan);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!panning.value) return;
      // Flushed rather than dropped, so the last sample before release still lands.
      if (panFrame) {
        cancelAnimationFrame(panFrame);
        commitPan();
      }
      panning.value = false;
      withPointerCapture(event, false);
    };

    // A frame requested by a drag that was still in progress would otherwise fire into a torn-down scope.
    onBeforeUnmount(() => {
      if (!panFrame) return;
      cancelAnimationFrame(panFrame);
      panFrame = 0;
    });

    /* ------------------------------------------------------------------ land raster */

    const resolvedDotDiameter = computed(() =>
      props.dotDiameter && props.dotDiameter > 0
        ? props.dotDiameter
        : Math.max(2.6, props.dotSpacing * 0.42)
    );

    /**
     * All polygon edges, flattened and bucketed by latitude.
     *
     * A scanline only ever meets edges that straddle its own latitude, so testing the full set once
     * per row wastes almost all of the work: at 1:50m resolution that is ~16k edges against ~60
     * rows on every pan frame. Bucketing turns each row into a scan of the few hundred edges that
     * can possibly cross it. Built once per dataset and shared between instances, never on pan or zoom.
     *
     * Edges are held in parallel flat arrays rather than as objects to keep the inner loop free of
     * property lookups and allocation.
     */
    const edgeIndex = computed(() =>
      cachedEdgeIndex(props.land, props.water, props.latSouth, props.latNorth)
    );

    /** Per-ring geometry, so the small-island pass does not re-walk every outline on every frame. */
    const landRingMetrics = computed(() => cachedRingMetrics(props.land));

    /**
     * Rasterises the outlines into a dot lattice with a scanline fill.
     *
     * Each lattice row is intersected with the edges that can cross it; the crossings are sorted and
     * taken in pairs as inside-spans (even-odd rule, which gives peninsulas and enclosed seas for
     * free). Dots land on a fixed screen-space grid inside those spans, so columns line up between
     * rows.
     */
    const landDots = computed<Dot[]>(() => {
      const { width, height } = size.value;
      const step = props.dotSpacing;
      const dots: Dot[] = [];
      if (width <= 0 || height <= 0 || step <= 0 || !(scale.value > 0)) return dots;

      const { lonA, latA, lonB, latB, buckets, south, span } = edgeIndex.value;

      /**
       * Every dot goes through here, and every dot therefore sits on the lattice exactly once.
       *
       * Both matter. Off-lattice dots — which is what a raw span midpoint or an island centroid is —
       * end up a fraction of a cell from their neighbours and read as a clump, which is far more
       * visible than the feature they were added for. And two rules can legitimately land on the
       * same cell, so without the dedupe you get overlapping circles that look heavier than the
       * rest of the field.
       */
      const columns = Math.floor((width - step / 2) / step) + 1;
      const taken = new Set<number>();
      const put = (column: number, row: number) => {
        if (column < 0 || row < 0 || column >= columns) return;
        const key = row * columns + column;
        if (taken.has(key)) return;
        const y = row * step + step / 2;
        if (y > height) return;
        taken.add(key);
        dots.push({ x: column * step + step / 2, y });
      };
      /** Nearest lattice index to a pixel coordinate. */
      const nearest = (position: number) => Math.round((position - step / 2) / step);

      for (let y = step / 2; y < height; y += step) {
        const lat = latFromY(y);
        if (lat > props.latNorth || lat < props.latSouth) continue;
        const row = nearest(y);

        const bucket =
          buckets[
            Math.min(LAT_BUCKETS - 1, Math.max(0, Math.floor(((lat - south) / span) * LAT_BUCKETS)))
          ];

        const crossings: number[] = [];
        for (let e = 0; e < bucket.length; e++) {
          const index = bucket[e];
          const lat1 = latA[index];
          const lat2 = latB[index];
          // Half-open comparison counts a vertex exactly once, avoiding doubled crossings.
          if ((lat1 <= lat && lat < lat2) || (lat2 <= lat && lat < lat1)) {
            const lon1 = lonA[index];
            crossings.push(lon1 + ((lat - lat1) / (lat2 - lat1)) * (lonB[index] - lon1));
          }
        }
        if (crossings.length < 2) continue;
        crossings.sort((a, b) => a - b);

        for (let i = 0; i + 1 < crossings.length; i += 2) {
          const xStart = projectX(crossings[i]);
          const xEnd = projectX(crossings[i + 1]);
          if (xEnd < 0 || xStart > width) continue;

          const first = Math.ceil((xStart - step / 2) / step);
          const last = Math.floor((xEnd - step / 2) / step);

          if (last < first) {
            // The span falls between two columns. Dropping it is how thin land — Japan, the
            // Indonesian chain, the Chilean coast — disappears from a dotted map, so it is rounded
            // to the nearer column instead: off by at most half a cell, and still on the lattice.
            //
            // Below a minimum width it is not thin land at all: a scanline through a ring's local
            // minimum yields two identical crossings, and two rings meeting at the antimeridian yield
            // two at longitude 180 — both zero-wide, and both would otherwise get a full-weight dot.
            if (xEnd - xStart <= step / MIN_SPAN_FRACTION) continue;
            put(nearest((xStart + xEnd) / 2), row);
            continue;
          }

          for (let k = first; k <= last; k++) put(k, row);
        }
      }

      // An island shorter than the gap between two rows is missed by every scanline. Where it is
      // still big enough on screen to deserve a dot, it gets one at its centre — that is what keeps
      // Java, Cuba, Taiwan and the like on the map at low zoom. The area test is what stops the same
      // rule from scattering the oceans with a speck for every islet in the dataset.
      const minScreenArea = (step * step) / 4;
      for (const ring of landRingMetrics.value) {
        if (ring.area * scale.value * scale.value < minScreenArea) continue;
        if ((ring.latMax - ring.latMin) * scale.value >= step) continue;

        // Only fill in when no lattice row actually crossed it, otherwise this doubles up a dot.
        const firstRow = Math.ceil((projectY(ring.latMax) - step / 2) / step);
        const lastRow = Math.floor((projectY(ring.latMin) - step / 2) / step);
        if (lastRow >= firstRow) continue;

        put(nearest(projectX(ring.lon)), nearest(projectY(ring.lat)));
      }

      return dots;
    });

    /* ------------------------------------------------------------------ land painting */

    /**
     * Paints the lattice onto the backing bitmap.
     *
     * The dot colour is read off the element's computed style rather than taken from the prop directly,
     * so a value like `rgb(var(--v-theme-indigo-light))` still resolves through the cascade — a canvas
     * `fillStyle` cannot resolve a custom property on its own. Every dot is one subpath of a single
     * `fill`, which is one rasterisation for the whole field.
     */
    const paintLand = () => {
      const element = landCanvas.value;
      // No 2d context in jsdom, and nothing to paint before the box has been measured.
      const context = element?.getContext?.("2d");
      if (!element || !context) return;

      const { width, height } = size.value;
      const ratio = window.devicePixelRatio || 1;
      element.width = Math.max(1, Math.round(width * ratio));
      element.height = Math.max(1, Math.round(height * ratio));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      const radius = resolvedDotDiameter.value / 2;
      context.fillStyle = window.getComputedStyle(element).color;
      context.beginPath();
      for (const dot of landDots.value) {
        context.moveTo(dot.x + radius, dot.y);
        context.arc(dot.x, dot.y, radius, 0, Math.PI * 2);
      }
      context.fill();
    };

    // Post-flush so the canvas element exists and carries its resolved colour before it is painted.
    watch([landDots, resolvedDotDiameter, landCanvas, () => props.dotColor], paintLand, {
      immediate: true,
      flush: "post",
    });

    /* ------------------------------------------------------------------ nodes and arcs */

    /** Nearest lattice coordinate to a pixel position — the same grid the land dots sit on. */
    const snap = (position: number) => {
      const step = props.dotSpacing;
      if (!props.snapNodes || !(step > 0)) return position;
      return Math.round((position - step / 2) / step) * step + step / 2;
    };

    /** How much room a marker takes, rings included — the radius two markers must clear to be legible. */
    const footprint = (node: DottedMapNode) =>
      Math.max(node.diameter ?? props.nodeDiameter, node.ringDiameter ?? 0) / 2;

    const visibleNodes = computed(() =>
      props.nodes.filter(
        (node) =>
          Number.isFinite(node.latitude) &&
          Number.isFinite(node.longitude) &&
          node.latitude <= props.latNorth &&
          node.latitude >= props.latSouth
      )
    );

    /**
     * Groups markers that would be drawn on top of each other.
     *
     * Two are merged when their circles actually overlap, rather than when they share a lattice cell:
     * cells are `dotSpacing` apart — a few pixels — while a marker is a couple of dozen across, so
     * neighbouring cells still cover each other almost completely and would leave the same unhittable
     * pile behind.
     *
     * <b>Measured before snapping, and that is the whole point.</b> The lattice is anchored to the
     * viewport, so a marker's snapped position hops from cell to cell as the map is panned and the
     * snapped gap between two fixed places swings by up to a whole cell. Deciding on those positions
     * made markers merge and split again while the user was merely dragging the map. Unsnapped, the
     * gap is `(lon₁ − lon₂) × scale`, which no amount of panning changes: the grouping is a function
     * of the zoom level alone, so it holds still under the hand and only ever changes on a zoom step.
     *
     * The anchor of a group is fixed once chosen, and chosen as the largest marker rather than
     * whichever happened to arrive first: the backend builds its city list by walking a hash set, so
     * its order is not stable between responses, and an order-sensitive rule would regroup the same
     * cities differently from one refresh to the next. A fixed anchor also bounds a group's extent,
     * where a centre that drifts towards each new member lets a row of places chain into one.
     */
    const clusters = computed(() => {
      interface Group {
        members: DottedMapNode[];
        clusterGroup?: string;
        anchorX: number;
        anchorY: number;
        anchorRadius: number;
        sumX: number;
        sumY: number;
        /** Where the first member sat in the caller's array, so draw order survives grouping. */
        order: number;
      }
      const groups: Group[] = [];

      const candidates = visibleNodes.value
        .map((node, order) => ({ node, order }))
        .sort(
          (a, b) => footprint(b.node) - footprint(a.node) || a.node.id.localeCompare(b.node.id)
        );

      for (const { node, order } of candidates) {
        const x = projectX(node.longitude);
        const y = projectY(node.latitude);
        const radius = footprint(node);

        const host = props.clusterNodes
          ? groups.find(
              (group) =>
                group.clusterGroup === node.clusterGroup &&
                Math.hypot(group.anchorX - x, group.anchorY - y) < group.anchorRadius + radius
            )
          : undefined;

        if (!host) {
          groups.push({
            members: [node],
            clusterGroup: node.clusterGroup,
            anchorX: x,
            anchorY: y,
            anchorRadius: radius,
            sumX: x,
            sumY: y,
            order,
          });
          continue;
        }
        host.members.push(node);
        host.sumX += x;
        host.sumY += y;
        host.order = Math.min(host.order, order);
      }

      // Drawn at the middle of its members and snapped there, so a merged marker still sits on the
      // lattice — a presentation step, deliberately downstream of every decision above.
      return groups
        .sort((a, b) => a.order - b.order)
        .map((group) => ({
          members: group.members,
          x: snap(group.sumX / group.members.length),
          y: snap(group.sumY / group.members.length),
        }));
    });

    const placedNodes = computed<RenderedNode[]>(() => {
      if (!(scale.value > 0)) return [];
      return clusters.value.map((group) => {
        // The largest member lends the cluster its styling: it is the one that would have been drawn
        // on top anyway, so merging never makes a marker smaller than what it replaced.
        const primary = group.members.reduce((a, b) => (footprint(b) > footprint(a) ? b : a));
        const diameter = primary.diameter ?? props.nodeDiameter;
        return {
          source: primary,
          sources: group.members,
          // Composite, so the key changes when membership does — a merged marker is not the same
          // thing as either of the markers it replaced.
          id: group.members.map((member) => member.id).join("|"),
          x: group.x,
          y: group.y,
          color: primary.color ?? props.nodeColor,
          diameter,
          hitDiameter: Math.max(diameter, props.nodeHitDiameter),
          ringDiameter: primary.ringDiameter ?? 0,
          ringColor: primary.ringColor ?? props.nodeRingColor,
          haloDiameter: primary.haloDiameter ?? 0,
          haloColor: primary.haloColor ?? props.nodeHaloColor ?? primary.color ?? props.nodeColor,
          haloOpacity: primary.haloOpacity ?? props.nodeHaloOpacity,
        };
      });
    });

    /**
     * The marker the pointer is over, re-resolved from the current set on every change.
     *
     * Resolving rather than remembering is what keeps the tooltip attached to its marker through a zoom
     * step or a data refresh — and what makes it disappear, rather than float over empty ocean, when the
     * marker it described is no longer there.
     */
    const hovered = computed<RenderedNode | null>(
      () => placedNodes.value.find((node) => node.id === hoveredId.value) ?? null
    );

    /** Every source id, merged ones included, so an arc still finds the marker standing for it. */
    const nodeById = computed(() => {
      const index = new Map<string, RenderedNode>();
      placedNodes.value.forEach((node) =>
        node.sources.forEach((source) => index.set(source.id, node))
      );
      return index;
    });

    /** A marker standing for more than one node, which the outline and the tooltip both key off. */
    const isCluster = (node: RenderedNode) => node.sources.length > 1;

    /**
     * How far above the marker the tooltip sits, measured from the marker's own edge.
     *
     * The tooltip is pulled up by its full height and then this much further, so the gap is the same
     * whatever it contains — a percentage offset instead scales with the content, which is why a
     * cluster listing several cities used to float far higher than a one-line one.
     *
     * The halo is deliberately not counted: it is a faint decoration around hubs, and clearing it
     * would push the tooltip away from every marker that has one.
     */
    const tooltipOffset = computed(() => {
      const node = hovered.value;
      if (!node) return 0;
      const visible = Math.max(
        node.diameter,
        node.ringDiameter,
        isCluster(node) ? node.diameter + 6 : 0
      );
      return visible / 2 + TOOLTIP_GAP;
    });

    /**
     * Quadratic Bezier bowed along the perpendicular of the chord, which gives the flight-path look
     * and keeps two links between the same pair visually distinct.
     */
    const arcPaths = computed(() => {
      // Merging markers merges their links too: several arcs now running between the same pair of
      // markers are one line, and an arc whose ends landed in the same marker has nowhere to go.
      // Both undo themselves on zoom, because the clusters do.
      const drawn = new Set<string>();
      return props.arcs
        .map((arc) => {
          const from = nodeById.value.get(arc.fromNodeId);
          const to = nodeById.value.get(arc.toNodeId);
          if (!from || !to || from === to) return null;
          const pair = `${from.id}->${to.id}`;
          if (drawn.has(pair)) return null;
          drawn.add(pair);
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const length = Math.hypot(dx, dy) || 1;
          const cx = (from.x + to.x) / 2 + (-dy / length) * props.arcBow * length;
          const cy = (from.y + to.y) / 2 + (dx / length) * props.arcBow * length;
          return {
            d: `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`,
            color: arc.color ?? props.arcColor,
            width: arc.width ?? props.arcWidth,
            opacity: arc.opacity ?? props.arcOpacity,
          };
        })
        .filter((arc): arc is NonNullable<typeof arc> => arc !== null);
    });

    // A zoom step rebuilds every marker, so whatever the pointer was over no longer exists — and a
    // cluster tooltip left behind would describe a grouping that has just been undone.
    watch(level, () => (hoveredId.value = null));

    return {
      viewport,
      landCanvas,
      size,
      hoveredId,
      hovered,
      landDots,
      resolvedDotDiameter,
      placedNodes,
      isCluster,
      tooltipOffset,
      arcPaths,
      level,
      canZoomIn,
      canZoomOut,
      zoomIn,
      zoomOut,
      canPan,
      panning,
      onPointerDown,
      onPointerMove,
      onPointerUp,
    };
  },
});
</script>

<style scoped lang="scss">
.dotted-world-map {
  position: relative;
  // The map fills whatever box the host gives it and letterboxes inside; it never drives layout
  // itself. overflow: hidden is what keeps a zoomed-in map from spilling out of that box.
  width: 100%;
  height: 100%;
  min-width: 0;
  overflow: hidden;
}

// Claimed only where a drag has somewhere to go. Applied unconditionally, it eats the touch gesture on
// a map that cannot pan, so a finger dragged across the card would not scroll the page.
.dotted-world-map--pannable {
  cursor: grab;
  touch-action: none;
}

.dotted-world-map--panning {
  cursor: grabbing;
}

.dotted-world-map__canvas,
.dotted-world-map__land {
  // Taken out of flow deliberately: an in-flow svg with pixel width/height would feed its own size
  // back into the box being measured, and the two would chase each other on resize.
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

// Decoration only. The drag is handled on the container, and the land must never be a pointer target.
.dotted-world-map__land {
  pointer-events: none;
}

.dotted-world-map__node {
  cursor: pointer;
}

.dotted-world-map__tooltip {
  position: absolute;
  // Exactly its own height, and no more: the clearance above the marker is applied to `top` in
  // pixels, so it does not grow with the tooltip's contents.
  transform: translate(-50%, -100%);
  pointer-events: none;
  z-index: 2;
}

.dotted-world-map__zoom {
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1;

  &--bottom-left {
    left: 8px;
    bottom: 8px;
  }
  &--bottom-right {
    right: 8px;
    bottom: 8px;
  }
  &--top-left {
    left: 8px;
    top: 8px;
  }
  &--top-right {
    right: 8px;
    top: 8px;
  }

  button {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgb(var(--v-theme-indigo-pale));
    border-radius: 4px;
    background: rgb(var(--v-theme-white));
    color: rgb(var(--v-theme-indigo-medium));
    font-size: 14px;
    line-height: 1;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: rgb(var(--v-theme-indigo-faint));
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
}
</style>
